import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { useCommentDraftStore } from "@/stores/comment-draft";
import { useReminderNotes, useCreateReminderNote } from "@/hooks/tasks";
import { Button } from "@/components/button";
import { FormTextarea } from "@/components/form";
import { formatShortDate } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
/** Comments section showing reminder notes with an inline composer. */
export function TaskDetailComments(_ref) {
  var _data$results;
  var reminderId = _ref.reminderId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient$pa, _s$selectedPatient;
    return (_s$selectedPatient$pa = (_s$selectedPatient = s.selectedPatient) === null || _s$selectedPatient === void 0 ? void 0 : _s$selectedPatient.patient_uid) !== null && _s$selectedPatient$pa !== void 0 ? _s$selectedPatient$pa : null;
  });
  var _useState = useState(false),
    showAll = _useState[0],
    setShowAll = _useState[1];
  var noteText = useCommentDraftStore(function (s) {
    var _s$drafts$reminderId;
    return (_s$drafts$reminderId = s.drafts[reminderId]) !== null && _s$drafts$reminderId !== void 0 ? _s$drafts$reminderId : "";
  });
  var setDraft = useCommentDraftStore(function (s) {
    return s.setDraft;
  });
  var clearDraft = useCommentDraftStore(function (s) {
    return s.clearDraft;
  });
  var _useReminderNotes = useReminderNotes(patientUid, reminderId),
    data = _useReminderNotes.data;
  var _useCreateReminderNot = useCreateReminderNote(patientUid, reminderId),
    createNote = _useCreateReminderNot.mutate,
    isSaving = _useCreateReminderNot.isLoading;
  var notes = (_data$results = data === null || data === void 0 ? void 0 : data.results) !== null && _data$results !== void 0 ? _data$results : [];
  var visibleLimit = 2;
  var hasMore = notes.length > visibleLimit;
  var visibleNotes = showAll ? notes : notes.slice(0, visibleLimit);
  var handleSave = function handleSave() {
    var trimmed = noteText.trim();
    if (!trimmed || isSaving) return;
    createNote(trimmed, {
      onSuccess: function onSuccess() {
        clearDraft(reminderId);
      }
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faComment,
    className: "w-4 h-4 text-sds-base-secondary"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.comments")), notes.length > 0 && /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-strong rounded-sds-full bg-sds-base-secondary text-sds-base-tertiary px-2 py-0.5"
  }, notes.length)), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3"
  }, hasMore && /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "task-detail-comments-toggle",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_COMMENTS_TOGGLE, {
        taskId: reminderId
      });
      setShowAll(function (v) {
        return !v;
      });
    },
    className: "flex items-center gap-1.5 text-style-label-strong text-sds-brand-default hover:text-sds-brand-secondary transition-colors"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: showAll ? faChevronUp : faChevronDown,
    className: "w-3 h-3"
  }), showAll ? t("button.collapse", {
    defaultValue: "Collapse"
  }) : t("button.view_all", {
    defaultValue: "View all"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "rounded-sds-200 border border-sds-base-default overflow-hidden"
  }, /*#__PURE__*/React.createElement(FormTextarea, {
    "data-testid": "task-detail-comment-input",
    value: noteText,
    minRows: 2,
    placeholder: t("label.reminder_add_comment_placeholder"),
    onChange: function onChange(e) {
      return setDraft(reminderId, e.currentTarget.value);
    },
    onBlur: function onBlur() {
      return trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_COMMENT_INPUT, {
        taskId: reminderId
      });
    },
    classNames: {
      input: "!border-0 !shadow-none !rounded-none"
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex justify-end px-3 py-2 bg-sds-base-secondary border-t border-sds-base-default"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "task-detail-comment-save",
    intent: "brand",
    variant: "primary",
    size: "sm",
    loading: isSaving,
    disabled: !noteText.trim(),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_DETAIL_COMMENT_SAVE, {
        taskId: reminderId
      });
      handleSave();
    }
  }, t("button.post", {
    defaultValue: "Post"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3 ".concat(showAll ? "max-h-72 overflow-y-auto scrollbar-sds pr-1" : "")
  }, visibleNotes.map(function (note) {
    var _ref2, _note$created_by_info, _note$created_by_info2, _note$created_by_info3;
    var authorName = (_ref2 = (_note$created_by_info = (_note$created_by_info2 = note.created_by_info) === null || _note$created_by_info2 === void 0 ? void 0 : _note$created_by_info2.name) !== null && _note$created_by_info !== void 0 ? _note$created_by_info : (_note$created_by_info3 = note.created_by_info) === null || _note$created_by_info3 === void 0 ? void 0 : _note$created_by_info3.email) !== null && _ref2 !== void 0 ? _ref2 : "";
    return /*#__PURE__*/React.createElement("div", {
      key: note.id,
      className: "rounded-sds-200 bg-sds-base-secondary px-4 py-3 flex flex-col gap-1.5"
    }, /*#__PURE__*/React.createElement("p", {
      className: "text-style-body-base text-sds-base-default break-words"
    }, note.note), /*#__PURE__*/React.createElement("p", {
      className: "text-style-label-base text-sds-base-tertiary"
    }, [authorName, formatShortDate(note.created_at)].filter(Boolean).join(" · ")));
  })));
}